import API_BASE from '../config';
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, Shield, Globe, Database, Users, Zap,
  FileCheck, HardDrive, UserCheck, Eye,
  ChevronRight, ChevronLeft, CheckCircle2, AlertTriangle,
  Smartphone, ShieldCheck, Building2, Cpu, Heart
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import ProgressBar from "../components/ProgressBar";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AssessmentFormData {
  // Stage 1
  company_name:          string;
  website_url:           string;
  business_description:  string;
  primary_country:       string;
  employee_count:      string;
  it_staff_count:      string;
  business_sector:     string;
  revenue_currency:    string;
  annual_revenue:      string;
  // Stage 2
  customer_records:    string;
  payment_card_data:   string;
  health_data:         string;
  children_data:       string;
  biometric_data:      string;
  data_classification: string;
  data_retention:      string;
  cross_border:        string;
  // Stage 3
  mfa_enabled:         string;
  fido2_mfa:           string;
  privileged_audit:    string;
  access_rights_review:string;
  password_manager:    string;
  zero_trust:          string;
  offboarding_process: string;
  background_checks:   string;
  // Stage 4
  cloud_provider:      string;
  siem_active:         string;
  cspm_scanning:       string;
  cloud_storage_audit: string;
  api_key_rotation:    string;
  oauth_token_review:  string;
  previous_breaches:   string;
  // Stage 5
  firewall_type:       string;
  network_segmentation:string;
  ids_ips:             string;
  dns_filtering:       string;
  dmarc_configured:    string;
  ddos_protection:     string;
  vpn_required:        string;
  remote_work_allowed: string;
  // Stage 6
  data_encryption:          string;
  encryption_in_transit:    string;
  patch_management:         string;
  edr_deployed:             string;
  hardware_encryption:      string;
  mobile_policy:            string;
  byod_policy:              string;
  dark_web_monitoring:      string;
  // Stage 7
  backup_frequency:    string;
  backup_type:         string;
  rto_goal:            string;
  dr_testing_freq:     string;
  disaster_recovery:   string;
  // Stage 8
  vendor_vetting:      string;
  vendor_count:        string;
  vendor_data_access:  string;
  vendor_soc2:         string;
  vendor_breach_clause:string;
  vendor_annual_review:string;
  // Stage 9
  employee_training:   string;
  phishing_simulation: string;
  bec_training:        string;
  bec_verification:    string;
  clean_desk_policy:   string;
  insider_dlp:         string;
  ueba_active:         string;
  // Stage 10
  api_protection:      string;
  api_rate_limiting:   string;
  api_pen_tested:      string;
  mobile_app:          string;
  mobile_pen_tested:   string;
  open_banking_api:    string;
  // Stage 11
  physical_access:     string;
  cctv_monitoring:     string;
  // Stage 12
  incident_plan:       string;
  irp_status:          string;
  ir_team_type:        string;
  insurance_status:    string;
  // Stage 13
  gdpr_status:         string;
  iso_27001:           string;
  soc2_status:         string;
  nist_framework:      string;
  hipaa_compliant:     string;
  dpo_appointed:       string;
  dpia_conducted:      string;
  breach_register:     string;
  compliance_audit:    string;
  zero_trust_policy:   string;
  // Stage 14
  has_nigeria_customers:    string;
  has_us_customers:         string;
  is_us_public:             string;
  has_uk_customers:         string;
  has_eu_customers:         string;
  has_canada_customers:     string;
  has_australia_customers:  string;
  has_uae_customers:        string;
  has_ksa_customers:        string;
  has_india_customers:      string;
  has_singapore_customers:  string;
  has_sa_customers:         string;
  // Stage 15 — sector specific
  aml_screening:            string;
  transaction_monitoring:   string;
  dormant_account_review:   string;
  dual_approval:            string;
  fraud_detection:          string;
  pci_dss:                  string;
  sanctions_screening:      string;
  medical_device_security:  string;
  ehr_access_controls:      string;
  patient_portal_security:  string;
  sdlc_security:            string;
  code_repo_access:         string;
  dependency_scanning:      string;
  bug_bounty:               string;
  ot_ics_segmented:         string;
  scada_security:           string;
  legacy_system_inventory:  string;
  property_data_security:   string;
  client_fund_controls:     string;
  wallet_security:          string;
  smart_contract_audit:     string;
  crypto_aml:               string;
  donor_data_protection:    string;
  grant_fund_controls:      string;
  beneficiary_data:         string;
  client_confidentiality:   string;
  matter_system_security:   string;
  student_data_policy:      string;
  edtech_supplier_review:   string;
  classified_data:          string;
  citizen_data_protection:  string;
  content_security:         string;
  supply_chain_security:    string;
  actuarial_data_security:  string;
  claims_fraud_detection:   string;
  // Stage 16
  segregation_duties:       string;
  insider_threat_prog:      string;
  account_lifecycle:        string;
  pen_test_frequency:       string;
  threat_intelligence:      string;
  red_team_exercise:        string;
  security_scorecard:       string;
}

// ─── Sections ─────────────────────────────────────────────────────────────────

const sections = [
  { id: 1,  title: "Entity Profile",         subtitle: "Company identity & classification",     icon: FileCheck   },
  { id: 2,  title: "Data Inventory",          subtitle: "Data volume, types & classification",   icon: Database    },
  { id: 3,  title: "Identity & Access",       subtitle: "Authentication & access controls",      icon: Lock        },
  { id: 4,  title: "Cloud Security",          subtitle: "Cloud infrastructure & posture",        icon: Globe       },
  { id: 5,  title: "Network Defense",         subtitle: "Perimeter, network & email security",   icon: Zap         },
  { id: 6,  title: "Data Protection",         subtitle: "Encryption, patching & device security",icon: Shield      },
  { id: 7,  title: "Recovery Ops",            subtitle: "Backup, DR & business continuity",      icon: HardDrive   },
  { id: 8,  title: "Vendor Risk",             subtitle: "Third-party ecosystem & supply chain",  icon: Users       },
  { id: 9,  title: "Human Firewall",          subtitle: "Personnel, training & BEC awareness",   icon: UserCheck   },
  { id: 10, title: "API & Mobile",            subtitle: "API security & mobile posture",         icon: Smartphone  },
  { id: 11, title: "Physical Assets",         subtitle: "Physical security controls",            icon: Eye         },
  { id: 12, title: "IR & Recovery",           subtitle: "Incident response readiness",           icon: ShieldCheck },
  { id: 13, title: "Global Compliance",       subtitle: "Regulatory framework alignment",        icon: Shield      },
  { id: 14, title: "International Ops",       subtitle: "Cross-border customers & regulations",  icon: Globe       },
  { id: 15, title: "Sector Controls",         subtitle: "Sector-specific security requirements", icon: Building2   },
  { id: 16, title: "Advanced Threats",        subtitle: "Emerging threat controls",              icon: Cpu         },
];

// ─── Default form state ───────────────────────────────────────────────────────

const defaultForm: AssessmentFormData = {
  company_name: "", website_url: "", business_description: "", primary_country: "", employee_count: "",
  it_staff_count: "", business_sector: "", revenue_currency: "USD", annual_revenue: "",
  customer_records: "", payment_card_data: "no", health_data: "no", children_data: "no",
  biometric_data: "no", data_classification: "no", data_retention: "no", cross_border: "no",
  mfa_enabled: "no", fido2_mfa: "no", privileged_audit: "no", access_rights_review: "no",
  password_manager: "no", zero_trust: "no", offboarding_process: "manual", background_checks: "no",
  cloud_provider: "aws", siem_active: "no", cspm_scanning: "no", cloud_storage_audit: "no",
  api_key_rotation: "no", oauth_token_review: "no", previous_breaches: "no",
  firewall_type: "basic", network_segmentation: "no", ids_ips: "no", dns_filtering: "no",
  dmarc_configured: "no", ddos_protection: "no", vpn_required: "no", remote_work_allowed: "no",
  data_encryption: "no", encryption_in_transit: "no", patch_management: "no", edr_deployed: "no",
  hardware_encryption: "no", mobile_policy: "no", byod_policy: "no", dark_web_monitoring: "no",
  backup_frequency: "daily", backup_type: "on_premise", rto_goal: "over_24", dr_testing_freq: "never",
  disaster_recovery: "no",
  vendor_vetting: "no", vendor_count: "1_5", vendor_data_access: "no", vendor_soc2: "no",
  vendor_breach_clause: "no", vendor_annual_review: "no",
  employee_training: "none", phishing_simulation: "no", bec_training: "no", bec_verification: "no",
  clean_desk_policy: "no", insider_dlp: "no", ueba_active: "no",
  api_protection: "no", api_rate_limiting: "no", api_pen_tested: "no", mobile_app: "no",
  mobile_pen_tested: "no", open_banking_api: "no",
  physical_access: "no", cctv_monitoring: "no",
  incident_plan: "no", irp_status: "none", ir_team_type: "none", insurance_status: "none",
  gdpr_status: "no", iso_27001: "no", soc2_status: "no", nist_framework: "no",
  hipaa_compliant: "no", dpo_appointed: "no", dpia_conducted: "no", breach_register: "no",
  compliance_audit: "no", zero_trust_policy: "no",
  has_nigeria_customers: "no", has_us_customers: "no", is_us_public: "no",
  has_uk_customers: "no", has_eu_customers: "no", has_canada_customers: "no",
  has_australia_customers: "no", has_uae_customers: "no", has_ksa_customers: "no",
  has_india_customers: "no", has_singapore_customers: "no", has_sa_customers: "no",
  aml_screening: "no", transaction_monitoring: "no", dormant_account_review: "no",
  dual_approval: "no", fraud_detection: "no", pci_dss: "no", sanctions_screening: "no",
  medical_device_security: "no", ehr_access_controls: "no", patient_portal_security: "no",
  sdlc_security: "no", code_repo_access: "no", dependency_scanning: "no", bug_bounty: "no",
  ot_ics_segmented: "no", scada_security: "no", legacy_system_inventory: "no",
  property_data_security: "no", client_fund_controls: "no",
  wallet_security: "no", smart_contract_audit: "no", crypto_aml: "no",
  donor_data_protection: "no", grant_fund_controls: "no", beneficiary_data: "no",
  client_confidentiality: "no", matter_system_security: "no",
  student_data_policy: "no", edtech_supplier_review: "no",
  classified_data: "no", citizen_data_protection: "no",
  content_security: "no", supply_chain_security: "no",
  actuarial_data_security: "no", claims_fraud_detection: "no",
  segregation_duties: "no", insider_threat_prog: "no", account_lifecycle: "no",
  pen_test_frequency: "never", threat_intelligence: "no", red_team_exercise: "no",
  security_scorecard: "no",
};


// ─── Sector helpers ───────────────────────────────────────────────────────────

const isFinancial = (s: string) => ["fintech","banking","insurance","crypto"].includes(s);
const isHealthcare = (s: string) => s === "healthcare";
const isTechnology = (s: string) => ["technology","fintech","crypto","banking"].includes(s);
const isManufacturing = (s: string) => ["manufacturing","energy"].includes(s);
const isLegal = (s: string) => ["legal","real_estate"].includes(s);
const isEducation = (s: string) => s === "education";
const isGovernment = (s: string) => s === "government";
const isMedia = (s: string) => s === "media";
const isLogistics = (s: string) => ["logistics","manufacturing"].includes(s);
const isCrypto = (s: string) => s === "crypto";
const isNGO = (s: string) => s === "ngo";
const isInsurance = (s: string) => s === "insurance";
const isRealEstate = (s: string) => s === "real_estate";
const usesCloud = (p: string) => p !== "none" && p !== "";
const hasAPI = (s: string) => ["fintech","banking","technology","retail","healthcare","insurance","crypto","government","education","ngo","logistics"].includes(s);
const isDataHeavy = (s: string) => ["fintech","banking","healthcare","legal","government","insurance","technology","crypto"].includes(s);
const needsBEC = (s: string) => ["fintech","banking","insurance","legal","real_estate","crypto","manufacturing","logistics","government"].includes(s);

// ─── Style constants ──────────────────────────────────────────────────────────

const inputCls = "w-full px-5 py-4 rounded-2xl border-2 border-slate-100 bg-white text-sm font-medium text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-red-400 transition-all";
const selectCls = "w-full px-5 py-4 rounded-2xl border-2 border-slate-100 bg-white text-sm font-medium text-slate-900 focus:outline-none focus:border-red-400 transition-all appearance-none cursor-pointer pr-12";
const selectStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat" as const,
  backgroundPosition: "right 1.25rem center",
};

function Label({ text, tooltip }: { text: string; tooltip?: string }) {
  return (
    <label className="text-[10px] font-black uppercase tracking-widest text-slate-700 flex items-center gap-1.5 mb-2">
      {text}
      {tooltip && (
        <span title={tooltip}
          className="w-4 h-4 rounded-full bg-slate-100 text-slate-400 text-[9px] font-black
            flex items-center justify-center hover:bg-red-50 hover:text-red-600
            transition-all border border-slate-200 cursor-help">
          i
        </span>
      )}
    </label>
  );
}

function SInput({ label, name, value, onChange, type = "text", placeholder, tooltip }: {
  label: string; name: string; value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string; placeholder?: string; tooltip?: string;
}) {
  return (
    <div>
      <Label text={label} tooltip={tooltip} />
      <input type={type} name={name} value={value} onChange={onChange}
        placeholder={placeholder} className={inputCls} />
    </div>
  );
}

function SSelect({ label, name, value, onChange, options, tooltip }: {
  label: string; name: string; value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[];
  tooltip?: string;
}) {
  return (
    <div>
      <Label text={label} tooltip={tooltip} />
      <select name={name} value={value} onChange={onChange}
        className={selectCls} style={selectStyle}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function YesNo({ label, name, value, onChange, tooltip }: {
  label: string; name: string; value: string;
  onChange: (val: string) => void; tooltip?: string;
}) {
  return (
    <div>
      <Label text={label} tooltip={tooltip} />
      <div className="flex gap-3">
        {[{ val: "yes", text: "Yes" }, { val: "no", text: "No" }].map(opt => {
          const sel = value === opt.val;
          return (
            <button key={opt.val} type="button" onClick={() => onChange(opt.val)}
              className={`flex-1 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest
                border-2 transition-all duration-150 ${
                sel
                  ? opt.val === "yes"
                    ? "bg-emerald-50 border-emerald-400 text-emerald-700"
                    : "bg-red-50 border-red-400 text-red-700"
                  : "bg-slate-50 border-slate-100 text-slate-400 hover:border-slate-300 hover:text-slate-600"
              }`}>
              {opt.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ─── Main Component ───────────────────────────────────────────────────────────

export default function Assessment() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState(0);
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [formData, setFormData]           = useState<AssessmentFormData>(defaultForm);
  const [showConsent, setShowConsent]     = useState(false);
  const [consent1, setConsent1]           = useState(false);
  const [consent2, setConsent2]           = useState(false);
  const [consent3, setConsent3]           = useState(false);
  const [showVendorPrompt, setShowVendorPrompt] = useState(false);
  const [vendors, setVendors]             = useState<{name:string;type:string}[]>([]);
  const [vendorName, setVendorName]       = useState("");
  const [vendorType, setVendorType]       = useState("cloud");

  const hi = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  const hy = (name: string, val: string) =>
    setFormData(p => ({ ...p, [name]: val }));

  const sector  = formData.business_sector;
  const country = formData.primary_country;
  const cloud   = formData.cloud_provider;

  const addVendor = () => {
    if (!vendorName.trim()) return;
    setVendors(prev => [...prev, { name: vendorName.trim(), type: vendorType }]);
    setVendorName(""); setVendorType("cloud");
  };

  const saveVendorsAndContinue = async () => {
    const token = localStorage.getItem("token");
    for (const v of vendors) {
      try {
        await axios.post(`${API_BASE}/api/vendors`,
          { name: v.name, vendor_type: v.type, has_soc2: false, has_iso27001: false, gdpr_compliant: true, recent_breach: false },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch { /* silent */ }
    }
    navigate("/dashboard");
  };

  const handleFinalize = async () => {
    if (!formData.company_name?.trim())   { alert("Please enter your Legal Entity Name in Stage 1."); setActiveSection(0); return; }
    if (!formData.primary_country)        { alert("Please select your Primary Country of Operation in Stage 1."); setActiveSection(0); return; }
    if (!formData.employee_count || Number(formData.employee_count) <= 0) { alert("Please enter your Total Employee Count in Stage 1."); setActiveSection(0); return; }
    if (!formData.business_sector)        { alert("Please select your Business Sector in Stage 1."); setActiveSection(0); return; }
    if (!formData.annual_revenue || Number(formData.annual_revenue) <= 0) { alert("Please enter your Estimated Annual Revenue in Stage 1."); setActiveSection(0); return; }
    if (!formData.revenue_currency)       { alert("Please select your Revenue Currency in Stage 1."); setActiveSection(0); return; }
    setConsent1(false); setConsent2(false); setConsent3(false);
    setShowConsent(true);
  };

  const handleConsentAndSubmit = async () => {
    if (!consent1 || !consent2 || !consent3) return;
    setShowConsent(false);
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_BASE}/api/drafts/save`,
        { form_data: formData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      queryClient.invalidateQueries({ queryKey: ["assessment"] });
      setShowVendorPrompt(true);
    } catch (err: any) {
      if (err.response?.status === 403) {
        alert("LICENSE LIMIT REACHED: Upgrade to Enterprise to audit additional companies.");
      } else {
        alert("SYNC ERROR: Please verify your session and try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const next = () => activeSection < sections.length - 1 ? setActiveSection(s => s + 1) : handleFinalize();
  const prev = () => activeSection > 0 && setActiveSection(s => s - 1);
  const cur = sections[activeSection];
  const CurIcon = cur.icon;


  return (
    <div className="min-h-screen bg-[#f8fafc] font-['Inter']">

      {/* ── VENDOR MODAL ──────────────────────────────────────────── */}
      <AnimatePresence>
        {showVendorPrompt && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4">
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[2.5rem] p-10 max-w-lg w-full shadow-2xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-emerald-50 border-2 border-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={24} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-emerald-600 mb-0.5">Assessment Complete</p>
                  <h2 className="text-xl font-black text-slate-900 uppercase tracking-tighter">One Final Step</h2>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-500 leading-relaxed mb-6">
                Add your key third-party vendors to complete your risk profile.
                This appears in your PDF report under <strong className="text-slate-900">Vendor Risk Management</strong>.
              </p>
              {vendors.length > 0 && (
                <div className="space-y-2 mb-4">
                  {vendors.map((v, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-2.5 border border-slate-100">
                      <div>
                        <p className="text-xs font-black text-slate-900 uppercase">{v.name}</p>
                        <p className="text-[9px] font-bold text-slate-400 uppercase">{v.type}</p>
                      </div>
                      <button onClick={() => setVendors(prev => prev.filter((_, j) => j !== i))}
                        className="text-slate-300 hover:text-red-500 transition-colors text-xs">✕</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mb-2">
                <input type="text" placeholder="Vendor name e.g. Stripe, AWS..."
                  value={vendorName} onChange={e => setVendorName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addVendor()}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-slate-200 text-sm font-medium text-slate-900 focus:border-red-400 focus:outline-none placeholder:text-slate-300" />
                <select value={vendorType} onChange={e => setVendorType(e.target.value)}
                  className="px-3 py-2.5 rounded-xl border-2 border-slate-200 text-xs font-black text-slate-700 focus:border-red-400 focus:outline-none bg-white">
                  <option value="cloud">Cloud</option>
                  <option value="payment">Payment</option>
                  <option value="saas">SaaS</option>
                  <option value="security">Security</option>
                  <option value="hr">HR</option>
                  <option value="legal">Legal</option>
                  <option value="other">Other</option>
                </select>
                <button onClick={addVendor} className="px-4 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-black hover:bg-slate-700 transition-all">+ Add</button>
              </div>
              <p className="text-[9px] font-bold text-slate-300 uppercase tracking-widest mb-6">Press Enter or click + Add after each vendor</p>
              <div className="flex gap-3">
                <button onClick={() => navigate("/dashboard")}
                  className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-400 font-black text-xs uppercase tracking-widest hover:border-slate-300 hover:text-slate-600 transition-all">
                  Skip for Now
                </button>
                <button onClick={saveVendorsAndContinue}
                  className="flex-1 py-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-red-100">
                  {vendors.length > 0 ? `Save ${vendors.length} Vendor${vendors.length > 1 ? "s" : ""} →` : "Go to Dashboard →"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CONSENT MODAL ─────────────────────────────────────────── */}
      <AnimatePresence>
        {showConsent && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4">
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[2.5rem] p-10 max-w-lg w-full shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-50 border-2 border-red-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={24} className="text-red-600" />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-red-600 mb-0.5">Required Consent</p>
                  <h2 className="text-xl font-black text-slate-900 uppercase tracking-tighter">Before We Proceed</h2>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-500 leading-relaxed mb-6">
                Please confirm the following before submitting your assessment for{" "}
                <strong className="text-slate-900">{formData.company_name || "your organisation"}</strong>.
              </p>
              {[
                { state: consent1, set: setConsent1, text: `I confirm that all data entered for ${formData.company_name || "this organisation"} is accurate and authorised for submission to VicShield Global for cybersecurity assessment purposes.` },
                { state: consent2, set: setConsent2, text: "I authorise VicShield to perform passive domain reconnaissance on the email domain provided, including port scanning and credential breach monitoring." },
                { state: consent3, set: setConsent3, text: "I understand that VicShield will generate a risk report and compliance radar based on this data, and that results are estimates based on provided information." },
              ].map((item, i) => (
                <div key={i} onClick={() => item.set(!item.state)}
                  className={`flex items-start gap-4 p-4 rounded-2xl border-2 cursor-pointer mb-3 transition-all ${item.state ? "border-emerald-400 bg-emerald-50" : "border-slate-100 bg-slate-50 hover:border-slate-300"}`}>
                  <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${item.state ? "border-emerald-500 bg-emerald-500" : "border-slate-300"}`}>
                    {item.state && <CheckCircle2 size={12} className="text-white" />}
                  </div>
                  <p className="text-xs font-medium text-slate-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowConsent(false)}
                  className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-400 font-black text-xs uppercase tracking-widest hover:border-slate-300 transition-all">
                  Back
                </button>
                <button onClick={handleConsentAndSubmit} disabled={!consent1 || !consent2 || !consent3 || isSubmitting}
                  className="flex-1 py-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-black text-xs uppercase tracking-widest transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed">
                  {isSubmitting ? "Processing..." : "Initialize Intelligence Audit →"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

      </AnimatePresence>

      {/* ── MAIN CONTENT ─────────────────────────────────────────── */}
      <div className="p-4 md:p-8 space-y-6">

        {/* Assessment Hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
          rounded-[3rem] p-8 shadow-2xl border border-white/5">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-1">
                Actuarial Intelligence
              </p>
              <h1 className="text-2xl font-black text-white uppercase tracking-tight">
                Risk Assessment
              </h1>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                16-Stage Cyber Risk & Compliance Audit
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-center">
                <p className="text-2xl font-black text-white">{activeSection + 1}<span className="text-sm text-slate-400">/{sections.length}</span></p>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-0.5">Stage</p>
              </div>
              <div className="bg-red-600/20 border border-red-500/30 rounded-2xl px-4 py-3 text-center">
                <p className="text-2xl font-black text-red-400">{Math.round((activeSection / sections.length) * 100)}%</p>
                <p className="text-[9px] font-black uppercase tracking-widest text-red-500 mt-0.5">Complete</p>
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            {sections.map((_, i) => (
              <button key={i}
                onClick={() => setActiveSection(i)}
                className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                  i < activeSection ? "bg-red-600" :
                  i === activeSection ? "bg-red-400" : "bg-white/10"
                }`}
              />
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Start</p>
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{sections[activeSection].title}</p>
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Complete</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-100/50 overflow-hidden">

          {/* Stage Header */}
          <div className="px-8 pt-6 pb-2 border-b border-slate-50 bg-slate-50/50">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-200">
                <CurIcon size={22} className="text-white" />
              </div>
              <div>
                <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-600 mb-0.5">Stage {cur.id} of {sections.length}</p>
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">{cur.title}</h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{cur.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Actuarial warning — ALL stages */}
          <div className="mx-8 mt-6 p-5 bg-amber-50 border border-amber-200 rounded-2xl flex gap-4">
            <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-1.5">Actuarial Integrity & Data Mandate</p>
              <p className="text-xs font-medium text-amber-700 leading-relaxed">
                The precision of your <strong>Risk Resilience Score</strong>, the validity of{" "}
                <strong>Financial Exposure quantification</strong>, and all{" "}
                <strong>Strategic Remediation outcomes</strong> are strictly contingent upon the accuracy of the data provided.
              </p>
              <p className="text-[10px] font-bold text-amber-600 mt-2 italic">
                Non-verified data will result in a compromised audit profile. Please ensure technical oversight during this assessment.
              </p>
            </div>
          </div>

          {/* Stage Content */}
          <AnimatePresence mode="wait">
            <motion.div key={activeSection}
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.18, ease: "easeOut" }}
              className="px-8 py-8">


              {/* ══ STAGE 1: ENTITY PROFILE ══ */}
              {activeSection === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <SInput label="Legal Entity Name *" name="company_name" value={formData.company_name} onChange={hi} placeholder="Enter full registered company name" />
                  </div>
                  <div className="md:col-span-2">
                    <SInput label="Primary Email Domain" name="website_url" value={formData.website_url} onChange={hi} placeholder="e.g. company.com" tooltip="Enter the domain used in company email addresses. e.g. if emails are             name@company.com enter: company.com" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                      What Does Your Company Do?
                      <span className="ml-2 text-slate-400 font-normal normal-case tracking-normal">
                        (This helps personalise your VicShield report)
                      </span>
                    </label>
                    <textarea
                      name="business_description"
                      value={formData.business_description}
                      onChange={hi as any}
                      rows={3}
                      placeholder="e.g. Briefly describe what your company does, your core products or services, and who your customers are."
                      className="w-full px-4 py-3 rounded-2xl border-2 border-slate-100
                        text-sm font-medium text-slate-900 bg-slate-50
                        focus:outline-none focus:border-red-400 focus:bg-white
                        transition-all resize-none placeholder:text-slate-300"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <SSelect label="Primary Country of Operation *" name="primary_country" value={formData.primary_country} onChange={hi}
                      tooltip="Select the country where your business is primarily incorporated and operates. This determines which regulatory frameworks apply."
                      options={[
                        { value: "",               label: "— Select your country —"              },
                        { value: "nigeria",        label: "🇳🇬  Nigeria"                          },
                        { value: "united_kingdom", label: "🇬🇧  United Kingdom"                   },
                        { value: "united_states",  label: "🇺🇸  United States"                    },
                        { value: "european_union", label: "🇪🇺  European Union (EU Member State)" },
                        { value: "canada",         label: "🇨🇦  Canada"                           },
                        { value: "australia",      label: "🇦🇺  Australia"                        },
                        { value: "uae",            label: "🇦🇪  United Arab Emirates"             },
                        { value: "saudi_arabia",   label: "🇸🇦  Saudi Arabia"                     },
                        { value: "india",          label: "🇮🇳  India"                            },
                        { value: "singapore",      label: "🇸🇬  Singapore"                        },
                        { value: "south_africa",   label: "🇿🇦  South Africa"                     },
                        { value: "other",          label: "🌍  Other / Multiple Countries"        },
                      ]} />
                  </div>
                  <SInput label="Total Employee Count *" name="employee_count" value={formData.employee_count} onChange={hi} type="number" placeholder="e.g. 250" />
                  <SInput label="Dedicated IT Staff" name="it_staff_count" value={formData.it_staff_count} onChange={hi} type="number" placeholder="e.g. 12" />
                  <div className="md:col-span-2">
                  <SSelect label="Business Sector *" name="business_sector" value={formData.business_sector} onChange={hi}
                    options={[
                      { value: "",              label: "— Select your sector —"            },
                      { value: "fintech",       label: "Fintech & Payments"                },
                      { value: "banking",       label: "Finance & Banking"                 },
                      { value: "insurance",     label: "Insurance"                         },
                      { value: "healthcare",    label: "Healthcare & Medical"              },
                      { value: "technology",    label: "Technology & SaaS"                 },
                      { value: "retail",        label: "Retail & E-Commerce"               },
                      { value: "manufacturing", label: "Manufacturing"                     },
                      { value: "legal",         label: "Legal & Professional Services"     },
                      { value: "education",     label: "Education"                         },
                      { value: "government",    label: "Government & Public Sector"        },
                      { value: "energy",        label: "Energy & Utilities"                },
                      { value: "media",         label: "Media & Communications"            },
                      { value: "logistics",     label: "Logistics & Supply Chain"          },
                      { value: "real_estate",   label: "Real Estate & Property"            },
                      { value: "crypto",        label: "Crypto / Web3 / Digital Assets"    },
                      { value: "ngo",           label: "NGO / Charity / Non-Profit"        },
                      { value: "other",         label: "Other"                             },
                    ]} />
                  </div>
                  <SSelect label="Revenue Currency *" name="revenue_currency" value={formData.revenue_currency} onChange={hi}
                    tooltip="Most used currencies appear first marked with ★"
                    options={[
                      { value: "USD", label: "★ USD — US Dollar"          },
                      { value: "GBP", label: "★ GBP — British Pound"      },
                      { value: "EUR", label: "★ EUR — Euro"               },
                      { value: "NGN", label: "★ NGN — Nigerian Naira"     },
                      { value: "AED", label: "★ AED — UAE Dirham"         },
                      { value: "AUD", label: "★ AUD — Australian Dollar"  },
                      { value: "CAD", label: "★ CAD — Canadian Dollar"    },
                      { value: "ZAR", label: "★ ZAR — South African Rand" },
                      { value: "INR", label: "★ INR — Indian Rupee"       },
                      { value: "SAR", label: "SAR — Saudi Riyal"          },
                      { value: "SGD", label: "SGD — Singapore Dollar"     },
                      { value: "BRL", label: "BRL — Brazilian Real"       },
                      { value: "CNY", label: "CNY — Chinese Yuan"         },
                      { value: "CHF", label: "CHF — Swiss Franc"          },
                      { value: "JPY", label: "JPY — Japanese Yen"         },
                      { value: "KES", label: "KES — Kenyan Shilling"      },
                      { value: "GHS", label: "GHS — Ghanaian Cedi"        },
                      { value: "MYR", label: "MYR — Malaysian Ringgit"    },
                      { value: "HKD", label: "HKD — Hong Kong Dollar"     },
                      { value: "NZD", label: "NZD — New Zealand Dollar"   },
                      { value: "SEK", label: "SEK — Swedish Krona"        },
                      { value: "NOK", label: "NOK — Norwegian Krone"      },
                      { value: "DKK", label: "DKK — Danish Krone"         },
                      { value: "PLN", label: "PLN — Polish Zloty"         },
                      { value: "TRY", label: "TRY — Turkish Lira"         },
                      { value: "QAR", label: "QAR — Qatari Riyal"         },
                      { value: "KWD", label: "KWD — Kuwaiti Dinar"        },
                      { value: "PHP", label: "PHP — Philippine Peso"      },
                      { value: "IDR", label: "IDR — Indonesian Rupiah"    },
                      { value: "PKR", label: "PKR — Pakistani Rupee"      },
                      { value: "BDT", label: "BDT — Bangladeshi Taka"     },
                      { value: "EGP", label: "EGP — Egyptian Pound"       },
                    ]} />
                  <div className="md:col-span-2">
                    <SInput label="Estimated Annual Revenue *" name="annual_revenue" value={formData.annual_revenue} onChange={hi} type="number" placeholder="e.g. 5000000" tooltip="Enter your estimated annual revenue in the selected currency. Used for financial exposure and fine calculations." />
                  </div>
                </div>
              )}

              {/* ══ STAGE 2: DATA INVENTORY ══ */}
              {activeSection === 1 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <SSelect label="Approximate Customer/User Records Held *" name="customer_records" value={formData.customer_records} onChange={hi}
                      tooltip="Estimate total number of personal records (customers, employees, users) your organisation holds. Used to calculate breach cost exposure."
                      options={[
                        { value: "",          label: "— Select record volume —"   },
                        { value: "under_1k",  label: "Under 1,000 records"        },
                        { value: "1k_10k",    label: "1,000 – 10,000 records"     },
                        { value: "10k_100k",  label: "10,000 – 100,000 records"   },
                        { value: "100k_1m",   label: "100,000 – 1 million records" },
                        { value: "over_1m",   label: "Over 1 million records"     },
                      ]} />
                  </div>
                  <YesNo label="Do You Process Payment Card Data?" name="payment_card_data" value={formData.payment_card_data} onChange={v => hy("payment_card_data", v)} tooltip="Credit/debit card processing triggers PCI-DSS requirements globally." />
                  <YesNo label="Do You Process Health or Medical Data?" name="health_data" value={formData.health_data} onChange={v => hy("health_data", v)} tooltip="Health data triggers HIPAA (US), NHIA (Nigeria), NHS DSPT (UK), GDPR Article 9 (EU)." />
                  <YesNo label="Do You Process Children's Data (Under 18)?" name="children_data" value={formData.children_data} onChange={v => hy("children_data", v)} tooltip="Children's data triggers COPPA (US), GDPR Article 8 (EU), NDPA (Nigeria), ICO guidance (UK)." />
                  <YesNo label="Do You Process Biometric Data?" name="biometric_data" value={formData.biometric_data} onChange={v => hy("biometric_data", v)} tooltip="Fingerprints, facial recognition, iris scans. Triggers GDPR Article 9, NDPA, BIPA (Illinois)." />
                  <YesNo label="Data Classification Policy in Place?" name="data_classification" value={formData.data_classification} onChange={v => hy("data_classification", v)} tooltip="Do you classify data as Public, Internal, Confidential or Restricted? Required by ISO 27001 A.8.2." />
                  <YesNo label="Formal Data Retention & Deletion Policy?" name="data_retention" value={formData.data_retention} onChange={v => hy("data_retention", v)} tooltip="Documented policy for how long data is kept and how it is securely deleted. Required by GDPR Article 5(e), NDPA." />
                  <div className="md:col-span-2">
                    <YesNo label="Do You Transfer Personal Data Across International Borders?" name="cross_border" value={formData.cross_border} onChange={v => hy("cross_border", v)} tooltip="Transferring personal data to another country triggers GDPR Chapter V, NDPA Section 43, UK GDPR adequacy requirements." />
                  </div>
                </div>
              )}

              {/* ══ STAGE 3: IDENTITY & ACCESS ══ */}
              {activeSection === 2 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <YesNo label="Multi-Factor Authentication (MFA)?" name="mfa_enabled" value={formData.mfa_enabled} onChange={v => hy("mfa_enabled", v)} tooltip="Is MFA enforced for all users accessing company systems? Required by CBN, DORA, FCA, NIST SP 800-63B." />
                  <YesNo label="Phishing-Resistant MFA (FIDO2/Hardware Key)?" name="fido2_mfa" value={formData.fido2_mfa} onChange={v => hy("fido2_mfa", v)} tooltip="Standard MFA (SMS/TOTP) can be phished. FIDO2 or hardware keys (YubiKey) are phishing-resistant. Recommended by CISA and DORA." />
                  <YesNo label="Privileged Access Audit Active?" name="privileged_audit" value={formData.privileged_audit} onChange={v => hy("privileged_audit", v)} tooltip="Do you audit who has admin/root access? Required by ISO 27001 A.9.2, CBN Cybersecurity Framework." />
                  <YesNo label="Periodic Access Rights Review (Quarterly)?" name="access_rights_review" value={formData.access_rights_review} onChange={v => hy("access_rights_review", v)} tooltip="Regular review of who has access to what systems. Required by ISO 27001 A.9.2.5, DORA, NITDA Guidelines." />
                  <YesNo label="Password Manager Enforced Company-Wide?" name="password_manager" value={formData.password_manager} onChange={v => hy("password_manager", v)} tooltip="Company-wide use of password managers (1Password, Bitwarden etc) significantly reduces credential theft risk. NIST SP 800-63B." />
                  <YesNo label="Zero Trust Architecture Adopted?" name="zero_trust" value={formData.zero_trust} onChange={v => hy("zero_trust", v)} tooltip="Never trust, always verify. Zero trust means no implicit trust based on network location. Required by DORA, CISA, NCSC." />
                  <SSelect label="Employee Offboarding Process" name="offboarding_process" value={formData.offboarding_process} onChange={hi}
                    tooltip="How are leavers' access rights revoked? Required by ISO 27001 A.7.3."
                    options={[
                      { value: "none",       label: "No Formal Process"                    },
                      { value: "manual",     label: "Manual — done on request"             },
                      { value: "checklist",  label: "Checklist — same-day revocation"      },
                      { value: "automated",  label: "Automated — instant revocation"       },
                    ]} />
                  <YesNo label="Background Checks Conducted on Staff?" name="background_checks" value={formData.background_checks} onChange={v => hy("background_checks", v)} tooltip="Pre-employment background screening. Required by ISO 27001 A.7.1.1, CBN Cybersecurity Framework." />
                </div>
              )}

              {/* ══ STAGE 4: CLOUD SECURITY ══ */}
              {activeSection === 3 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <SSelect label="Primary Cloud Provider *" name="cloud_provider" value={formData.cloud_provider} onChange={hi}
                      options={[
                        { value: "aws",        label: "Amazon Web Services (AWS)"   },
                        { value: "azure",      label: "Microsoft Azure"             },
                        { value: "gcp",        label: "Google Cloud Platform (GCP)" },
                        { value: "oracle",     label: "Oracle Cloud"                },
                        { value: "ibm",        label: "IBM Cloud"                   },
                        { value: "multi",      label: "Multi-Cloud"                 },
                        { value: "private",    label: "Private / On-Premise Only"   },
                        { value: "none",       label: "No Cloud — Fully On-Premise" },
                      ]} />
                  </div>
                  <YesNo label="SIEM Active Monitoring?" name="siem_active" value={formData.siem_active} onChange={v => hy("siem_active", v)} tooltip="Security Information and Event Management. Real-time log aggregation and alerting. Required by ISO 27001 A.12.4, CBN Framework, DORA." />
                  <YesNo label="Previous Security Breaches (Last 24 Months)?" name="previous_breaches" value={formData.previous_breaches} onChange={v => hy("previous_breaches", v)} tooltip="Prior breaches significantly increase risk score and insurance premiums. Required disclosure in most frameworks." />
                  {usesCloud(cloud) && (
                    <>
                      <YesNo label="Cloud Security Posture (CSPM) Scanning?" name="cspm_scanning" value={formData.cspm_scanning} onChange={v => hy("cspm_scanning", v)} tooltip="Tools like AWS Security Hub, Prisma Cloud that scan for cloud misconfigurations. Reduces breach risk by up to 60%. CIS Cloud Benchmarks." />
                      <YesNo label="Cloud/S3 Storage Access Audit?" name="cloud_storage_audit" value={formData.cloud_storage_audit} onChange={v => hy("cloud_storage_audit", v)} tooltip="Regular audit of who can access cloud storage buckets/blobs. Exposed S3 buckets are a leading cause of data breaches." />
                    </>
                  )}
                  {(isFinancial(sector) || isTechnology(sector)) && usesCloud(cloud) && (
                    <>
                      <YesNo label="API Key Rotation Policy Enforced?" name="api_key_rotation" value={formData.api_key_rotation} onChange={v => hy("api_key_rotation", v)} tooltip="Regular rotation of API keys and secrets. OWASP API Security Top 10 — API2: Broken Authentication." />
                      <YesNo label="OAuth Token & API Credential Review?" name="oauth_token_review" value={formData.oauth_token_review} onChange={v => hy("oauth_token_review", v)} tooltip="Regular review of OAuth tokens and API credentials for expiry and least privilege. OWASP API Security Top 10." />
                    </>
                  )}
                </div>
              )}

              {/* ══ STAGE 5: NETWORK DEFENSE ══ */}
              {activeSection === 4 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <SSelect label="Firewall Architecture" name="firewall_type" value={formData.firewall_type} onChange={hi}
                    tooltip="What type of firewall protection do you have? ISO 27001 A.13.1."
                    options={[
                      { value: "none",     label: "None / Unknown"                    },
                      { value: "basic",    label: "Basic — Router Firewall Only"      },
                      { value: "ngfw",     label: "Next-Gen Firewall (NGFW)"          },
                      { value: "waf",      label: "WAF + NGFW (Web Application)"      },
                      { value: "zerotrust",label: "Zero Trust Network Access (ZTNA)"  },
                    ]} />
                  <YesNo label="Network Segmentation Implemented?" name="network_segmentation" value={formData.network_segmentation} onChange={v => hy("network_segmentation", v)} tooltip="Are different systems isolated in separate network zones? Limits breach spread. Required by NIST CSF, NIS2, CBN Framework." />
                  <YesNo label="Intrusion Detection/Prevention (IDS/IPS)?" name="ids_ips" value={formData.ids_ips} onChange={v => hy("ids_ips", v)} tooltip="Active monitoring for malicious network activity. Required by ISO 27001 A.12.6, NIS2, NITDA." />
                  <YesNo label="DNS Filtering Active?" name="dns_filtering" value={formData.dns_filtering} onChange={v => hy("dns_filtering", v)} tooltip="Blocking malicious domains at DNS level. Stops malware callbacks and phishing. CISA SCuBA recommendation." />
                  <YesNo label="DMARC/SPF/DKIM Email Authentication?" name="dmarc_configured" value={formData.dmarc_configured} onChange={v => hy("dmarc_configured", v)} tooltip="Email authentication prevents domain spoofing and BEC attacks. Required by NIST SP 800-177, NCSC (UK), NITDA (Nigeria)." />
                  <YesNo label="DDoS Protection Active?" name="ddos_protection" value={formData.ddos_protection} onChange={v => hy("ddos_protection", v)} tooltip="Protection against Distributed Denial of Service attacks. Required by ISO 27001 A.17, NCC Framework (Nigeria)." />
                  <YesNo label="VPN Required for Remote Access?" name="vpn_required" value={formData.vpn_required} onChange={v => hy("vpn_required", v)} tooltip="All remote workers must connect via VPN. NIST SP 800-77 guidance." />
                  <YesNo label="Remote Work Permitted?" name="remote_work_allowed" value={formData.remote_work_allowed} onChange={v => hy("remote_work_allowed", v)} tooltip="Does your organisation permit employees to work from home or remote locations?" />
                </div>
              )}

              {/* ══ STAGE 6: DATA PROTECTION ══ */}
              {activeSection === 5 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <YesNo label="Data Encryption at Rest?" name="data_encryption" value={formData.data_encryption} onChange={v => hy("data_encryption", v)} tooltip="All data at rest encrypted with AES-256 or equivalent. Required by NDPA, CBN, GDPR Article 32, HIPAA, GLBA." />
                  <YesNo label="Data Encrypted in Transit (HTTPS/TLS)?" name="encryption_in_transit" value={formData.encryption_in_transit} onChange={v => hy("encryption_in_transit", v)} tooltip="All data in transit encrypted using TLS 1.2+ or HTTPS. Required by GDPR Article 32, NDPA, ISO 27001 A.10." />
                  <YesNo label="Patch Management Programme?" name="patch_management" value={formData.patch_management} onChange={v => hy("patch_management", v)} tooltip="Systematic process for applying security patches. ACSC Essential Eight — high risk patches within 48 hours." />
                  <YesNo label="EDR Solution Deployed?" name="edr_deployed" value={formData.edr_deployed} onChange={v => hy("edr_deployed", v)} tooltip="Endpoint Detection and Response (CrowdStrike, SentinelOne, Microsoft Defender). Detects and responds to endpoint threats. Required by DORA, ACSC." />
                  <YesNo label="Endpoint Hardening (BitLocker/FileVault)?" name="hardware_encryption" value={formData.hardware_encryption} onChange={v => hy("hardware_encryption", v)} tooltip="Full disk encryption on all devices. CIS Controls 3.6, ACSC Essential Eight." />
                  <YesNo label="Mobile Device Policy (MDM)?" name="mobile_policy" value={formData.mobile_policy} onChange={v => hy("mobile_policy", v)} tooltip="Mobile Device Management controls for company and BYOD devices. ISO 27001 A.6.2." />
                  <YesNo label="BYOD Policy Enforced?" name="byod_policy" value={formData.byod_policy} onChange={v => hy("byod_policy", v)} tooltip="Bring Your Own Device policy covering personal devices used for work. ISO 27001 A.6.2." />
                  <YesNo label="Dark Web Credential Monitoring Active?" name="dark_web_monitoring" value={formData.dark_web_monitoring} onChange={v => hy("dark_web_monitoring", v)} tooltip="Monitoring dark web sources for leaked company credentials. NIST CSF ID.RA — proactive threat intelligence." />
                </div>
              )}

              {/* ══ STAGE 7: RECOVERY OPS ══ */}
              {activeSection === 6 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <SSelect label="Backup Frequency" name="backup_frequency" value={formData.backup_frequency} onChange={hi}
                    options={[
                      { value: "real_time", label: "Real-Time / Continuous"  },
                      { value: "daily",     label: "Daily"                   },
                      { value: "weekly",    label: "Weekly"                  },
                      { value: "monthly",   label: "Monthly"                 },
                      { value: "none",      label: "No Regular Backups"      },
                    ]} />
                  <SSelect label="Backup Architecture *" name="backup_type" value={formData.backup_type} onChange={hi}
                    tooltip="Immutable and offsite backups are the gold standard against ransomware. Required by ISO 27001 A.12.3, ACSC Essential Eight."
                    options={[
                      { value: "on_premise", label: "On-Premise Only"                   },
                      { value: "cloud",      label: "Cloud-Sync Only"                   },
                      { value: "hybrid",     label: "Hybrid (On-Premise + Cloud)"       },
                      { value: "immutable",  label: "Immutable & Offsite (Air-Gapped)"  },
                    ]} />
                  <SSelect label="Target Recovery Time (RTO)" name="rto_goal" value={formData.rto_goal} onChange={hi}
                    tooltip="Maximum acceptable downtime after a critical incident. Required by ISO 22301, CBN BCP Requirements, DORA."
                    options={[
                      { value: "over_24",  label: "24+ Hours"                 },
                      { value: "under_24", label: "Less than 24 Hours"        },
                      { value: "under_4",  label: "Less than 4 Hours (Elite)" },
                      { value: "under_1",  label: "Less than 1 Hour (Tier 1)" },
                    ]} />
                  <SSelect label="DR Testing Frequency" name="dr_testing_freq" value={formData.dr_testing_freq} onChange={hi}
                    tooltip="How often do you test disaster recovery plans? Required by ISO 22301, DORA Article 11."
                    options={[
                      { value: "never",     label: "Never Tested"       },
                      { value: "annually",  label: "Annually"           },
                      { value: "biannual",  label: "Every 6 Months"     },
                      { value: "quarterly", label: "Quarterly"          },
                    ]} />
                  <div className="md:col-span-2">
                    <YesNo label="Business Continuity Plan (BCP) Documented?" name="disaster_recovery" value={formData.disaster_recovery} onChange={v => hy("disaster_recovery", v)} tooltip="Formal BCP covering how critical operations continue during disruption. Required by ISO 22301, CBN BCP, DORA, PRA Operational Resilience." />
                  </div>
                </div>
              )}

              {/* ══ STAGE 8: VENDOR RISK ══ */}
              {activeSection === 7 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <YesNo label="Formal Vendor Vetting Process?" name="vendor_vetting" value={formData.vendor_vetting} onChange={v => hy("vendor_vetting", v)} tooltip="Do you security-assess vendors before onboarding? Required by ISO 27001 A.15, DORA Chapter V, CBN Framework." />
                  <SSelect label="Number of Critical Third-Party Vendors" name="vendor_count" value={formData.vendor_count} onChange={hi}
                    tooltip="Vendors with access to your systems or data. Supply chain attacks hit 15% of organisations. DORA and CBN require vendor risk management."
                    options={[
                      { value: "none",   label: "None"         },
                      { value: "1_5",    label: "1 – 5"        },
                      { value: "6_20",   label: "6 – 20"       },
                      { value: "21_50",  label: "21 – 50"      },
                      { value: "over_50",label: "Over 50"      },
                    ]} />
                  <YesNo label="Do Vendors Have Access to Your Data?" name="vendor_data_access" value={formData.vendor_data_access} onChange={v => hy("vendor_data_access", v)} tooltip="If vendors process personal data on your behalf they are data processors — requires GDPR Article 28 / NDPA Section 44 contracts." />
                  <YesNo label="Do Key Vendors Hold SOC2/ISO 27001?" name="vendor_soc2" value={formData.vendor_soc2} onChange={v => hy("vendor_soc2", v)} tooltip="Vendors with SOC2 Type II or ISO 27001 certification demonstrate verified security controls. ISO 27001 A.15.2." />
                  <YesNo label="Vendor Breach Notification Clause in Contracts?" name="vendor_breach_clause" value={formData.vendor_breach_clause} onChange={v => hy("vendor_breach_clause", v)} tooltip="Contract clause requiring vendors to notify you of breaches within 24-72 hours. Required by GDPR Article 28, NDPA Section 44." />
                  <YesNo label="Annual Vendor Security Review Conducted?" name="vendor_annual_review" value={formData.vendor_annual_review} onChange={v => hy("vendor_annual_review", v)} tooltip="Annual review of all critical vendors' security posture. Required by ISO 27001 A.15.2, DORA, CBN Framework." />
                </div>
              )}

              {/* ══ STAGE 9: HUMAN FIREWALL ══ */}
              {activeSection === 8 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <SSelect label="Security Awareness Training" name="employee_training" value={formData.employee_training} onChange={hi}
                    tooltip="Regular security training for all staff. Required by ISO 27001 A.7.2.2, NITDA, NIS2 Article 20."
                    options={[
                      { value: "none",      label: "No Training Programme"     },
                      { value: "annually",  label: "Annual Training"           },
                      { value: "quarterly", label: "Quarterly Training"        },
                      { value: "monthly",   label: "Monthly Training"          },
                      { value: "continuous",label: "Continuous Learning Platform" },
                    ]} />
                  <YesNo label="Phishing Simulation Conducted?" name="phishing_simulation" value={formData.phishing_simulation} onChange={v => hy("phishing_simulation", v)} tooltip="Simulated phishing campaigns to test staff awareness. NIST SP 800-50 — significantly reduces successful phishing rates." />
                  <YesNo label="BEC/Social Engineering Awareness Training?" name="bec_training" value={formData.bec_training} onChange={v => hy("bec_training", v)} tooltip="Specific training on Business Email Compromise, deepfake calls, and CEO fraud. FBI IC3 BEC guidance." />
                  {needsBEC(sector) && (
                    <YesNo label="Wire Transfer Callback Verification Policy?" name="bec_verification" value={formData.bec_verification} onChange={v => hy("bec_verification", v)} tooltip="Policy requiring phone callback to verify large wire transfers. Prevents BEC fraud. FBI IC3 and EFCC guidance." />
                  )}
                  <YesNo label="Clean Desk Policy Enforced?" name="clean_desk_policy" value={formData.clean_desk_policy} onChange={v => hy("clean_desk_policy", v)} tooltip="Staff required to clear desks and lock screens when away. ISO 27001 A.11.2.9." />
                  {isDataHeavy(sector) && (
                    <>
                      <YesNo label="DLP (Data Loss Prevention) Deployed?" name="insider_dlp" value={formData.insider_dlp} onChange={v => hy("insider_dlp", v)} tooltip="Tools monitoring and blocking unauthorised data exfiltration (email, USB, cloud upload). Required by NIST SP 800-171, NDPA, GDPR." />
                      <YesNo label="User Behaviour Analytics (UEBA) Active?" name="ueba_active" value={formData.ueba_active} onChange={v => hy("ueba_active", v)} tooltip="AI-based monitoring of user behaviour to detect insider threats and compromised accounts. Different from SIEM — NIST CSF DE.CM-3." />
                    </>
                  )}
                </div>
              )}

              {/* ══ STAGE 10: API & MOBILE ══ */}
              {activeSection === 9 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {hasAPI(sector) && (
                    <>
                      <YesNo label="API Security Protection Active?" name="api_protection" value={formData.api_protection} onChange={v => hy("api_protection", v)} tooltip="API gateway, authentication and rate limiting. OWASP API Security Top 10." />
                      <YesNo label="API Rate Limiting Enforced?" name="api_rate_limiting" value={formData.api_rate_limiting} onChange={v => hy("api_rate_limiting", v)} tooltip="Prevents API abuse and DDoS. OWASP API4 — Lack of Rate Limiting." />
                      <YesNo label="API Penetration Tested (Last 12 Months)?" name="api_pen_tested" value={formData.api_pen_tested} onChange={v => hy("api_pen_tested", v)} tooltip="External pen test of all API endpoints. Required by CBN Framework (Nigeria), MAS TRM (Singapore), OWASP." />
                    </>
                  )}
                  {isFinancial(sector) && (
                    <YesNo label="Open Banking API Security Controls?" name="open_banking_api" value={formData.open_banking_api} onChange={v => hy("open_banking_api", v)} tooltip="Specific security controls for open banking APIs. Required by CBN Open Banking Framework (Nigeria), FCA (UK), PSD2/PSD3 (EU)." />
                  )}
                  <YesNo label="Do You Have a Published Mobile App?" name="mobile_app" value={formData.mobile_app} onChange={v => hy("mobile_app", v)} tooltip="iOS or Android app published on app stores." />
                  {formData.mobile_app === "yes" && (
                    <YesNo label="Mobile App Security Tested (OWASP MASVS)?" name="mobile_pen_tested" value={formData.mobile_pen_tested} onChange={v => hy("mobile_pen_tested", v)} tooltip="Mobile app penetration testing against OWASP Mobile Application Security Verification Standard." />
                  )}
                  {!hasAPI(sector) && formData.mobile_app !== "yes" && (
                    <div className="md:col-span-2 text-center py-8 text-slate-400">
                      <Smartphone size={32} className="mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-bold">No API or mobile-specific questions for your sector.</p>
                      <p className="text-xs mt-1">Proceed to the next stage.</p>
                    </div>
                  )}
                </div>
              )}

              {/* ══ STAGE 11: PHYSICAL ASSETS ══ */}
              {activeSection === 10 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <YesNo label="Facility Entry Protocol (Keycard/Badge)?" name="physical_access" value={formData.physical_access} onChange={v => hy("physical_access", v)} tooltip="Controlled physical access to offices and data centres. Required by ISO 27001 A.11.1." />
                  <YesNo label="CCTV Monitoring Active?" name="cctv_monitoring" value={formData.cctv_monitoring} onChange={v => hy("cctv_monitoring", v)} tooltip="CCTV surveillance of critical areas. Required by ISO 27001 A.11.1.2." />
                </div>
              )}

              {/* ══ STAGE 12: IR & RECOVERY ══ */}
              {activeSection === 11 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <YesNo label="Formal Incident Response Plan?" name="incident_plan" value={formData.incident_plan} onChange={v => hy("incident_plan", v)} tooltip="Documented plan for responding to cyber incidents. Required by ISO 27001 A.16, CBN, NIS2, NITDA, NIST SP 800-61." />
                  <SSelect label="IRP Documentation Status" name="irp_status" value={formData.irp_status} onChange={hi}
                    tooltip="How developed is your Incident Response Plan?"
                    options={[
                      { value: "none",       label: "No Formal Plan"                     },
                      { value: "documented", label: "Documented Only"                    },
                      { value: "tested",     label: "Documented & Tested Annually"       },
                      { value: "exercised",  label: "Regularly Exercised (Tabletop/Live)"},
                    ]} />
                  <SSelect label="IR Team Structure *" name="ir_team_type" value={formData.ir_team_type} onChange={hi}
                    tooltip="Who handles incidents? NIST SP 800-61."
                    options={[
                      { value: "none",      label: "No Dedicated IR Team"              },
                      { value: "internal",  label: "Internal IT Team Only"             },
                      { value: "hybrid",    label: "Internal + External Retainer"      },
                      { value: "mssp",      label: "Managed Security Service (MSSP)"   },
                      { value: "dedicated", label: "Dedicated CSIRT/SOC"               },
                    ]} />
                  <SSelect label="Cyber Insurance Status" name="insurance_status" value={formData.insurance_status} onChange={hi}
                    tooltip="CBN mandates cyber insurance for Nigerian banks. NAIC Model Law for US insurers. FCA guidance for UK firms."
                    options={[
                      { value: "none",          label: "No Active Coverage"        },
                      { value: "basic",         label: "Basic Liability Only"      },
                      { value: "comprehensive", label: "Full Comprehensive Policy" },
                    ]} />
                </div>
              )}

              {/* ══ STAGE 13: GLOBAL COMPLIANCE ══ */}
              {activeSection === 12 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <YesNo label="GDPR / Data Privacy Compliant?" name="gdpr_status" value={formData.gdpr_status} onChange={v => hy("gdpr_status", v)} tooltip="Applies to all companies processing EU or UK personal data. Fines up to €20M or 4% of global turnover." />
                  <YesNo label="ISO 27001 Certified?" name="iso_27001" value={formData.iso_27001} onChange={v => hy("iso_27001", v)} tooltip="International information security management certification. Highly valued by enterprise clients and insurers." />
                  {(isFinancial(sector) || isTechnology(sector) || isGovernment(sector)) && (
                    <YesNo label="SOC 2 Type II Certified?" name="soc2_status" value={formData.soc2_status} onChange={v => hy("soc2_status", v)} tooltip="AICPA security controls certification. Required by most US enterprise clients. Reduces insurance premiums." />
                  )}
                  <YesNo label="NIST Cybersecurity Framework Adopted?" name="nist_framework" value={formData.nist_framework} onChange={v => hy("nist_framework", v)} tooltip="NIST CSF 2.0 — the global cybersecurity baseline adopted by enterprises worldwide." />
                  {isHealthcare(sector) && (
                    <YesNo label="HIPAA Compliant?" name="hipaa_compliant" value={formData.hipaa_compliant} onChange={v => hy("hipaa_compliant", v)} tooltip="US Health Insurance Portability and Accountability Act. Mandatory for US healthcare and health data processors." />
                  )}
                  <YesNo label="Data Protection Officer (DPO) Appointed?" name="dpo_appointed" value={formData.dpo_appointed} onChange={v => hy("dpo_appointed", v)} tooltip="Required by GDPR Article 37, NDPA Section 30, UK GDPR Article 37 for certain organisations." />
                  <YesNo label="DPIA Conducted for High-Risk Processing?" name="dpia_conducted" value={formData.dpia_conducted} onChange={v => hy("dpia_conducted", v)} tooltip="Data Protection Impact Assessment. Required by GDPR Article 35, NDPA Section 28 for high-risk data processing." />
                  <YesNo label="Data Breach Register Maintained?" name="breach_register" value={formData.breach_register} onChange={v => hy("breach_register", v)} tooltip="Record of all personal data breaches. Required by GDPR Article 33, NDPA, UK GDPR Article 33." />
                  <YesNo label="Compliance Audit Within Last 12 Months?" name="compliance_audit" value={formData.compliance_audit} onChange={v => hy("compliance_audit", v)} tooltip="Independent compliance review. Required by ISO 27001 A.18, DORA, CBN + NDPA." />
                  <div className="md:col-span-2">
                    <YesNo label="Zero Trust Security Policy Documented?" name="zero_trust_policy" value={formData.zero_trust_policy} onChange={v => hy("zero_trust_policy", v)} tooltip="Formal zero trust policy approved by leadership. Required by CISA, DORA, NCSC." />
                  </div>
                </div>
              )}

              {/* ══ STAGE 14: INTERNATIONAL OPERATIONS ══ */}
              {activeSection === 13 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <p className="md:col-span-2 text-xs font-medium text-slate-500 bg-slate-50 rounded-2xl p-4 border border-slate-100">
                    Select all countries where you have customers, operations or data subjects.
                    Each selection triggers the applicable regulatory frameworks for that jurisdiction.
                  </p>
                  <YesNo label="🇳🇬 Nigerian Customers or Operations?" name="has_nigeria_customers" value={formData.has_nigeria_customers} onChange={v => hy("has_nigeria_customers", v)} tooltip="Triggers: NDPA 2023, Nigeria Cybercrimes Act, CBN Framework, SEC Nigeria, NCC, NAICOM, EFCC/SCUML" />
                  <YesNo label="🇺🇸 US Customers or Operations?" name="has_us_customers" value={formData.has_us_customers} onChange={v => hy("has_us_customers", v)} tooltip="Triggers: HIPAA, GLBA, CCPA, SEC, FISMA, NERC CIP, CMMC, FTC, NAIC" />
                  <YesNo label="🇬🇧 UK Customers or Operations?" name="has_uk_customers" value={formData.has_uk_customers} onChange={v => hy("has_uk_customers", v)} tooltip="Triggers: UK GDPR, FCA, PRA, Cyber Essentials, NHS DSPT, Ofcom, Ofgem NIS, SRA" />
                  <YesNo label="🇪🇺 EU Customers or Operations?" name="has_eu_customers" value={formData.has_eu_customers} onChange={v => hy("has_eu_customers", v)} tooltip="Triggers: GDPR, DORA, NIS2, EU AI Act, PSD2/PSD3, MiCA, MDR, Solvency II" />
                  <YesNo label="🇨🇦 Canadian Customers or Operations?" name="has_canada_customers" value={formData.has_canada_customers} onChange={v => hy("has_canada_customers", v)} tooltip="Triggers: PIPEDA/Bill C-27, OSFI B-13, Quebec Law 25, FINTRAC" />
                  <YesNo label="🇦🇺 Australian Customers or Operations?" name="has_australia_customers" value={formData.has_australia_customers} onChange={v => hy("has_australia_customers", v)} tooltip="Triggers: Privacy Act/APP, APRA CPS 234, ACSC Essential Eight, SOCI Act" />
                  <YesNo label="🇦🇪 UAE/GCC Customers or Operations?" name="has_uae_customers" value={formData.has_uae_customers} onChange={v => hy("has_uae_customers", v)} tooltip="Triggers: UAE PDPL, CBUAE Cybersecurity Framework, DIFC/ADGM Data Protection" />
                  <YesNo label="🇸🇦 Saudi Arabia Customers or Operations?" name="has_ksa_customers" value={formData.has_ksa_customers} onChange={v => hy("has_ksa_customers", v)} tooltip="Triggers: Saudi NCA ECC, Saudi PDPL, SAMA Cybersecurity Framework" />
                  <YesNo label="🇮🇳 Indian Customers or Operations?" name="has_india_customers" value={formData.has_india_customers} onChange={v => hy("has_india_customers", v)} tooltip="Triggers: DPDP Act 2023, RBI Cybersecurity Framework, CERT-In Directions, SEBI, IRDAI" />
                  <YesNo label="🇸🇬 Singapore Customers or Operations?" name="has_singapore_customers" value={formData.has_singapore_customers} onChange={v => hy("has_singapore_customers", v)} tooltip="Triggers: PDPA, MAS TRM Guidelines, CSA Cybersecurity Act" />
                  <YesNo label="🇿🇦 South African Customers or Operations?" name="has_sa_customers" value={formData.has_sa_customers} onChange={v => hy("has_sa_customers", v)} tooltip="Triggers: POPIA, SARB Cybersecurity Framework, FSCA Regulations" />
                  <div className="col-span-2">
                    <YesNo label="🇺🇸 Listed on a US Stock Exchange?" name="is_us_public" value={formData.is_us_public} onChange={v => hy("is_us_public", v)} tooltip="NYSE or NASDAQ listing triggers SEC cybersecurity disclosure rules — mandatory incident reporting within 4 business days." />
                  </div>
                </div>
              )}

              {/* ══ STAGE 15: SECTOR-SPECIFIC CONTROLS ══ */}
              {activeSection === 14 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    {!sector ? (
                      <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 text-center">
                        <AlertTriangle size={28} className="text-red-500 mx-auto mb-3" />
                        <p className="text-sm font-black text-red-700 uppercase tracking-tight mb-2">
                          Business Sector Not Selected
                        </p>
                        <p className="text-xs font-medium text-red-600 leading-relaxed mb-4">
                          Sector-specific questions require your business sector to be selected in Stage 1.
                          Please go back and complete Stage 1 — Entity Profile before continuing.
                        </p>
                        <button
                          onClick={() => setActiveSection(0)}
                          className="px-6 py-2.5 bg-red-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-red-700 transition-all">
                          ← Go Back to Stage 1
                        </button>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                        <p className="text-xs font-medium text-slate-500">
                          <strong className="text-blue-700">Sector: {sector.replace(/_/g," ").toUpperCase()}</strong> — Questions below are specific to your sector and country.
                          Only questions relevant to <strong className="text-slate-700">{sector.replace(/_/g," ")}</strong> are shown.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Financial sectors */}
                  {isFinancial(sector) && (
                    <>
                      <YesNo label="AML/CFT Screening System Active?" name="aml_screening" value={formData.aml_screening} onChange={v => hy("aml_screening", v)} tooltip="Anti-Money Laundering screening. Required by: EFCC/SCUML (Nigeria), FinCEN/BSA (US), FCA/POCA (UK), AMLD6 (EU), MAS Notice 626 (Singapore)." />
                      <YesNo label="Real-Time Transaction Monitoring?" name="transaction_monitoring" value={formData.transaction_monitoring} onChange={v => hy("transaction_monitoring", v)} tooltip="Automated monitoring of transactions for fraud and AML. Required by CBN Framework (Nigeria), FinCEN (US), FCA (UK), AMLD6 (EU)." />
                      <YesNo label="Dormant Account Review Policy?" name="dormant_account_review" value={formData.dormant_account_review} onChange={v => hy("dormant_account_review", v)} tooltip="Policy for reviewing and managing inactive accounts. Required by CBN Guidelines (Nigeria), FCA CASS (UK), OCC Guidelines (US). Dormant accounts are exploited for fraud." />
                      <YesNo label="Dual Approval for Large Transfers?" name="dual_approval" value={formData.dual_approval} onChange={v => hy("dual_approval", v)} tooltip="Two-person approval for large financial transfers prevents fraud and BEC. SWIFT CSP requirement, CBN Framework (Nigeria)." />
                      <YesNo label="Fraud Detection System Active?" name="fraud_detection" value={formData.fraud_detection} onChange={v => hy("fraud_detection", v)} tooltip="Automated fraud detection on transactions. Required by CBN + EFCC (Nigeria), FinCEN (US), FCA (UK), PSD2 (EU)." />
                      <YesNo label="PCI-DSS Compliance Status?" name="pci_dss" value={formData.pci_dss} onChange={v => hy("pci_dss", v)} tooltip="Payment Card Industry Data Security Standard. Mandatory for all organisations processing card payments globally. Fines $5K-$100K/month." />
                      <YesNo label="Sanctions Screening Active?" name="sanctions_screening" value={formData.sanctions_screening} onChange={v => hy("sanctions_screening", v)} tooltip="Screening against OFAC, UN, EU and CBN sanctions lists. Required for all financial institutions globally." />
                    </>
                  )}

                  {/* Healthcare */}
                  {isHealthcare(sector) && (
                    <>
                      <YesNo label="Medical Device Cybersecurity Controls?" name="medical_device_security" value={formData.medical_device_security} onChange={v => hy("medical_device_security", v)} tooltip="Security controls for connected medical devices. Required by HIPAA + FDA (US), EU MDR/IVDR, NAFDAC (Nigeria), MHRA + CQC (UK)." />
                      <YesNo label="EHR/EMR Access Controls?" name="ehr_access_controls" value={formData.ehr_access_controls} onChange={v => hy("ehr_access_controls", v)} tooltip="Electronic Health/Medical Record access controls and audit logs. Required by HIPAA (US), NHS DSPT (UK), NHIA (Nigeria), GDPR Article 9 (EU)." />
                      <div className="md:col-span-2">
                        <YesNo label="Patient Portal Security Controls?" name="patient_portal_security" value={formData.patient_portal_security} onChange={v => hy("patient_portal_security", v)} tooltip="Security of online patient access portals. Required by HIPAA (US), CQC (UK), NHIA (Nigeria)." />
                      </div>
                    </>
                  )}

                  {/* Technology */}
                  {isTechnology(sector) && (
                    <>
                      <YesNo label="Secure SDLC Practices Implemented?" name="sdlc_security" value={formData.sdlc_security} onChange={v => hy("sdlc_security", v)} tooltip="Security integrated into software development lifecycle. OWASP SAMM, NIST SSDF, EU AI Act (if AI product)." />
                      <YesNo label="Code Repository Access Controls?" name="code_repo_access" value={formData.code_repo_access} onChange={v => hy("code_repo_access", v)} tooltip="Access controls on GitHub/GitLab/Bitbucket. Developer credential theft leads to supply chain attacks. CIS Controls 14." />
                      <YesNo label="Open Source/Dependency Scanning?" name="dependency_scanning" value={formData.dependency_scanning} onChange={v => hy("dependency_scanning", v)} tooltip="Automated scanning of open source dependencies for vulnerabilities (OWASP Dependency-Check, Snyk). CISA SBOM guidance." />
                      <YesNo label="Bug Bounty or Responsible Disclosure Programme?" name="bug_bounty" value={formData.bug_bounty} onChange={v => hy("bug_bounty", v)} tooltip="Programme inviting security researchers to find and report vulnerabilities. ISO 29147. Enterprise clients increasingly require this." />
                    </>
                  )}

                  {/* Manufacturing / Energy */}
                  {isManufacturing(sector) && (
                    <>
                      <YesNo label="OT/ICS Network Segmented from IT?" name="ot_ics_segmented" value={formData.ot_ics_segmented} onChange={v => hy("ot_ics_segmented", v)} tooltip="Operational Technology and Industrial Control Systems isolated from corporate IT. Required by IEC 62443, NERC CIP (US), NIS2 Energy (EU), NERC Nigeria." />
                      <YesNo label="SCADA System Security Controls?" name="scada_security" value={formData.scada_security} onChange={v => hy("scada_security", v)} tooltip="Supervisory Control and Data Acquisition system security. Required by IEC 62443, NERC CIP (US), NIS2 (EU). Critical infrastructure protection." />
                      <div className="md:col-span-2">
                        <YesNo label="Legacy System Inventory Maintained?" name="legacy_system_inventory" value={formData.legacy_system_inventory} onChange={v => hy("legacy_system_inventory", v)} tooltip="Documented inventory of legacy/end-of-life systems. Legacy systems are primary attack vectors. NIST CSF ID.AM, CISA guidance." />
                      </div>
                    </>
                  )}

                  {/* Real Estate */}
                  {isRealEstate(sector) && (
                    <>
                      <YesNo label="Property Transaction Data Security?" name="property_data_security" value={formData.property_data_security} onChange={v => hy("property_data_security", v)} tooltip="Security of property transaction data. Required by EFCC/SCUML AML (Nigeria), HMRC AML (UK), FinCEN Real Estate (US)." />
                      <YesNo label="Client Fund Segregation Controls?" name="client_fund_controls" value={formData.client_fund_controls} onChange={v => hy("client_fund_controls", v)} tooltip="Segregation of client funds from operating funds. Required by EFCC/NBA (Nigeria), SRA Accounts Rules (UK), State Bar Rules (US)." />
                    </>
                  )}

                  {/* Crypto / Web3 */}
                  {isCrypto(sector) && (
                    <>
                      <YesNo label="Crypto Wallet & Key Management Security?" name="wallet_security" value={formData.wallet_security} onChange={v => hy("wallet_security", v)} tooltip="Security of crypto wallets, private keys and custody. Required by CCSS, FinCEN (US), MiCA (EU), CBN/SEC Nigeria." />
                      <YesNo label="Smart Contract Security Audit Conducted?" name="smart_contract_audit" value={formData.smart_contract_audit} onChange={v => hy("smart_contract_audit", v)} tooltip="Independent security audit of smart contracts before deployment. CWE vulnerability standards, MiCA (EU)." />
                      <YesNo label="Crypto-Specific AML/KYC Controls?" name="crypto_aml" value={formData.crypto_aml} onChange={v => hy("crypto_aml", v)} tooltip="AML and KYC controls specific to crypto transactions. Required by FATF Travel Rule, TFR/MiCA (EU), CBN Circular (Nigeria), FinCEN (US)." />
                    </>
                  )}

                  {/* NGO / Charity */}
                  {isNGO(sector) && (
                    <>
                      <YesNo label="Donor Data Protection Controls?" name="donor_data_protection" value={formData.donor_data_protection} onChange={v => hy("donor_data_protection", v)} tooltip="Security of donor personal and financial data. Required by ICO/Charity Commission (UK), GDPR (EU), NDPA (Nigeria)." />
                      <YesNo label="Grant/Fund Management Security Controls?" name="grant_fund_controls" value={formData.grant_fund_controls} onChange={v => hy("grant_fund_controls", v)} tooltip="Security controls for grant management systems. Required by Charity Commission (UK), IRS 990 (US), CAMA 2020 (Nigeria)." />
                      <div className="md:col-span-2">
                        <YesNo label="Beneficiary Data Protection Policy?" name="beneficiary_data" value={formData.beneficiary_data} onChange={v => hy("beneficiary_data", v)} tooltip="Protection of beneficiary personal data. Required by GDPR/data protection laws globally, NDPA (Nigeria)." />
                      </div>
                    </>
                  )}

                  {/* Legal */}
                  {isLegal(sector) && (
                    <>
                      <YesNo label="Client Data Confidentiality Controls?" name="client_confidentiality" value={formData.client_confidentiality} onChange={v => hy("client_confidentiality", v)} tooltip="Technical controls protecting client privilege and confidentiality. Required by NBA Rules (Nigeria), SRA Code (UK), ABA Model Rules (US), GDPR." />
                      <YesNo label="Matter Management System Security?" name="matter_system_security" value={formData.matter_system_security} onChange={v => hy("matter_system_security", v)} tooltip="Security of legal matter management systems (iManage, NetDocuments etc). ISO 27001 baseline, NBA/SRA requirements." />
                    </>
                  )}

                  {/* Education */}
                  {isEducation(sector) && (
                    <>
                      <YesNo label="Student Data Protection Policy?" name="student_data_policy" value={formData.student_data_policy} onChange={v => hy("student_data_policy", v)} tooltip="Policy protecting student personal data. Required by FERPA (US), ICO Education guidance (UK), NDPA + TETFUND (Nigeria), GDPR (EU)." />
                      <YesNo label="EdTech Supplier Security Agreements?" name="edtech_supplier_review" value={formData.edtech_supplier_review} onChange={v => hy("edtech_supplier_review", v)} tooltip="Data processing agreements with EdTech suppliers. Required by ICO Education (UK), FERPA (US), NITDA (Nigeria)." />
                    </>
                  )}

                  {/* Government */}
                  {isGovernment(sector) && (
                    <>
                      <YesNo label="Classified/Sensitive Government Data Controls?" name="classified_data" value={formData.classified_data} onChange={v => hy("classified_data", v)} tooltip="Security controls for classified government data. Required by NITDA Gov Policy (Nigeria), NCSC/GDS (UK), FISMA/FedRAMP (US), EU Cybersecurity Act." />
                      <YesNo label="Citizen Data Protection Controls?" name="citizen_data_protection" value={formData.citizen_data_protection} onChange={v => hy("citizen_data_protection", v)} tooltip="Protection of citizen personal data held by government. Required by NDPA + NITDA (Nigeria), UK GDPR + GDS (UK), GDPR (EU), Privacy Act 1974 (US)." />
                    </>
                  )}

                  {/* Media */}
                  {isMedia(sector) && (
                    <>
                      <YesNo label="Digital Content Protection Controls?" name="content_security" value={formData.content_security} onChange={v => hy("content_security", v)} tooltip="DRM and content security controls. Required by NBC/NCC (Nigeria), Ofcom (UK), AVMSD (EU)." />
                    </>
                  )}

                  {/* Logistics */}
                  {isLogistics(sector) && (
                    <div className="md:col-span-2">
                      <YesNo label="Supply Chain Cybersecurity Controls?" name="supply_chain_security" value={formData.supply_chain_security} onChange={v => hy("supply_chain_security", v)} tooltip="Cybersecurity across the supply chain. Required by ISO 28000, Customs/NIMASA (Nigeria), NIS2 (EU), CISA C-SCRM (US)." />
                    </div>
                  )}

                  {/* Insurance */}
                  {isInsurance(sector) && (
                    <>
                      <YesNo label="Actuarial/Claims Data Security?" name="actuarial_data_security" value={formData.actuarial_data_security} onChange={v => hy("actuarial_data_security", v)} tooltip="Security of actuarial data and claims systems. Required by NAICOM (Nigeria), PRA/FCA (UK), NAIC (US), Solvency II (EU)." />
                      <YesNo label="Claims Fraud Detection System?" name="claims_fraud_detection" value={formData.claims_fraud_detection} onChange={v => hy("claims_fraud_detection", v)} tooltip="Automated detection of fraudulent insurance claims. Required by NAICOM (Nigeria), FCA (UK), NAIC (US), Solvency II (EU)." />
                    </>
                  )}

                  {/* Fallback — sector selected but no specific questions match */}
                  {sector && !isFinancial(sector) && !isHealthcare(sector) && !isTechnology(sector) &&
                   !isManufacturing(sector) && !isLegal(sector) && !isEducation(sector) &&
                   !isGovernment(sector) && !isMedia(sector) && !isLogistics(sector) &&
                   !isCrypto(sector) && !isNGO(sector) && !isInsurance(sector) && !isRealEstate(sector) && (
                    <div className="md:col-span-2 bg-slate-50 rounded-2xl p-6 border border-slate-100 text-center">
                      <p className="text-sm font-black text-slate-700 uppercase tracking-tight mb-2">
                        General Sector Controls Applied
                      </p>
                      <p className="text-xs font-medium text-slate-500 leading-relaxed">
                        Your sector uses universal security controls covered in earlier stages.
                        No additional sector-specific questions apply. You may proceed to the next stage.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ══ STAGE 16: ADVANCED THREATS ══ */}
              {activeSection === 15 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {isDataHeavy(sector) && (
                    <>
                      <YesNo label="Segregation of Duties Enforced?" name="segregation_duties" value={formData.segregation_duties} onChange={v => hy("segregation_duties", v)} tooltip="No single person can complete a critical task alone. Prevents insider fraud. Required by ISO 27001 A.6.1.2, CBN Framework, DORA." />
                      <YesNo label="Insider Threat Programme Active?" name="insider_threat_prog" value={formData.insider_threat_prog} onChange={v => hy("insider_threat_prog", v)} tooltip="Formal programme to detect and respond to insider threats. CISA Insider Threat guidance, NISPOM (US), CBN Framework (Nigeria)." />
                      <YesNo label="Automated Account Lifecycle Management?" name="account_lifecycle" value={formData.account_lifecycle} onChange={v => hy("account_lifecycle", v)} tooltip="Automated provisioning and deprovisioning of user accounts. Eliminates orphaned accounts. ISO 27001 A.9.2, DORA." />
                    </>
                  )}
                  <SSelect label="Penetration Testing Frequency" name="pen_test_frequency" value={formData.pen_test_frequency} onChange={hi}
                    tooltip="How often do you commission external penetration testing? Required by ISO 27001 A.18.2, CBN Framework, DORA TLPT, MAS TRM (Singapore), FCA (UK)."
                    options={[
                      { value: "never",     label: "Never"               },
                      { value: "annually",  label: "Annually"            },
                      { value: "biannual",  label: "Every 6 Months"      },
                      { value: "quarterly", label: "Quarterly"           },
                      { value: "continuous",label: "Continuous (Bug Bounty + Testing)" },
                    ]} />
                  <YesNo label="Threat Intelligence Feed Active?" name="threat_intelligence" value={formData.threat_intelligence} onChange={v => hy("threat_intelligence", v)} tooltip="Subscription to threat intelligence feeds (MISP, ISAC, commercial). Required by NIST CSF ID.RA-3, DORA, NITDA (Nigeria)." />
                  <YesNo label="Security Scorecard / TPRM Platform Used?" name="security_scorecard" value={formData.security_scorecard} onChange={v => hy("security_scorecard", v)} tooltip="Third-party risk rating platform (SecurityScorecard, BitSight, UpGuard). Monitors vendor security continuously. ISO 27001 A.15, DORA Chapter V." />
                  {(isFinancial(sector) || isTechnology(sector) || isGovernment(sector)) && (
                    <div className="md:col-span-2">
                      <YesNo label="Red Team Exercise Conducted (Last 12 Months)?" name="red_team_exercise" value={formData.red_team_exercise} onChange={v => hy("red_team_exercise", v)} tooltip="Adversarial simulation of real attacks. Required by DORA TIBER-EU, CBEST/STAR (UK), CBN Framework (Nigeria), MAS AASE (Singapore)." />
                    </div>
                  )}
                </div>
              )}


            </motion.div>
          </AnimatePresence>

          {/* ── NAVIGATION FOOTER ─────────────────────────────────── */}
          <div className="flex items-center justify-between px-8 py-5 border-t border-slate-50 bg-slate-50/50">
            <button onClick={prev}
              className={`flex items-center gap-2 text-[11px] font-black uppercase tracking-widest
                px-5 py-3 rounded-xl transition-all ${
                activeSection === 0
                  ? "opacity-0 pointer-events-none"
                  : "text-slate-400 hover:text-slate-700 hover:bg-white border border-transparent hover:border-slate-200"
              }`}>
              <ChevronLeft size={15} /> Previous
            </button>

            {/* Progress dots */}
            <div className="flex gap-1.5">
              {sections.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === activeSection ? "w-6 bg-red-600" :
                  i < activeSection   ? "w-1.5 bg-red-200" :
                                        "w-1.5 bg-slate-200"
                }`} />
              ))}
            </div>

            <button onClick={next} disabled={isSubmitting}
              className="flex items-center gap-3 bg-red-600 text-white px-8 py-4
                rounded-2xl font-black text-[11px] uppercase tracking-widest
                hover:bg-slate-900 transition-all shadow-lg shadow-red-100/50
                disabled:opacity-50 disabled:cursor-not-allowed active:scale-95">
              {isSubmitting
                ? "Processing..."
                : activeSection === sections.length - 1
                ? "Initialize Intelligence Audit"
                : "Commit & Continue"}
              {!isSubmitting && <ChevronRight size={15} />}
            </button>
        </div>
      </div>
      </div>
    </div>
  );
}